import React, { useState } from 'react';
import {
    Alert,
    Box,
    Typography,
    Paper,
    IconButton,
    Tooltip,
    Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatBooleanValue, formatDisplayValue } from '../../../utils/tagValueUtils';
import { formatDateOnly } from '../../../utils/timestamps';
import { countNoun } from '../../../utils/pluralise';
import { formatDeclaredDate, isDateType } from '../../../utils/excelExport';
import { POLICY_LABELS, RECENT_WEIGHTED_POLICY } from '../../../utils/tableSettings';
import FileReviewDialog from '../manage/FileReviewDialog';

// The grid's number format (DataTable.js), so a number reads the same here as in
// the cell that was clicked.
const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const shareFormatter = new Intl.NumberFormat('en-GB', {
    style: 'percent',
    maximumFractionDigits: 1
});

// The policy the endpoint answers with for an Add metric. Its cell is the sum of
// every file's values, so every value counts and none is marked as the chosen one.
const ADD_POLICY = 'Add';

// The column policies by the names Table Settings gives them (utils/tableSettings.js),
// and the Add metric's update behaviour.
const POLICY_NAMES = { ...POLICY_LABELS, [ADD_POLICY]: 'Add' };

// Marks a stated value that equals the belief: the value the column's policy
// chooses from these files now, which is not always the value the grid shows
// (Spec 4 section 7).
const POLICY_CHOICE_MARK = "The value the column's policy chooses from these files";

const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return formatBooleanValue(value);
    if (typeof value === 'number') return numberFormatter.format(value);
    return String(value);
};

// One file that states the cell: its reporting date, its sum for an Add metric,
// the values it states with how many of its rows state each, and its tables with
// their first source rows. A table opens the file's review dialog at that table.
function FileEntry({ entry, belief, isAdd, onOpenUnit }) {
    const otherValueCount = entry.value_count - entry.values.length;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
                {entry.file_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Reporting date {formatDateOnly(entry.file_date)}
            </Typography>
            {isAdd && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Adds {formatValue(entry.add)}
                </Typography>
            )}

            <Stack spacing={0.5} sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                {entry.values.map((stated, index) => {
                    const isPolicyChoice = !isAdd && stated.value === belief;
                    return (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isPolicyChoice && (
                                <Tooltip title={POLICY_CHOICE_MARK}>
                                    <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                                </Tooltip>
                            )}
                            <Typography variant="body2" fontWeight={isPolicyChoice ? 600 : 400}>
                                {formatValue(stated.value)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {countNoun(stated.rows, 'row')} · {shareFormatter.format(stated.share)}
                            </Typography>
                        </Box>
                    );
                })}
                {otherValueCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        and {countNoun(otherValueCount, 'other value')} · {shareFormatter.format(entry.other_share)} of rows
                    </Typography>
                )}
            </Stack>

            {entry.units.map((unit) => (
                <Box key={unit.unit_id} sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={500}>
                            {unit.unit_name ?? 'Unknown table'} · {countNoun(unit.row_count, 'row')}
                        </Typography>
                        <Tooltip title="Open the file this came from">
                            <IconButton size="small" onClick={() => onOpenUnit(entry.file_id, unit.unit_id)}>
                                <OpenInNewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {unit.rows.map((row, index) => {
                        const isPolicyChoice = !isAdd && row.value === belief;
                        return (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {row.page != null ? `Page ${row.page}, record ${row.record}` : `Row ${row.row}`}
                                    {row.period_label ? ` · ${row.period_label}` : ''}
                                </Typography>
                                <Typography variant="caption" fontWeight={isPolicyChoice ? 600 : 400}>
                                    {formatValue(row.value)}
                                </Typography>
                                {isPolicyChoice && (
                                    <Tooltip title={POLICY_CHOICE_MARK}>
                                        <CheckCircleIcon color="success" sx={{ fontSize: 14 }} />
                                    </Tooltip>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Paper>
    );
}

// The current lineage of a cell, as services/lineage.js::fetchCellLineage
// answers it: the column's policy and its explanation of the cell's value, then
// every file that states the cell, in the table's row order. `currentValue` is
// the value the grid shows for the cell (CellLineageDrawer's `cellInfo`).
function LineageTimeline({ lineage, currentValue, companyId, tableId, table }) {
    // The file review dialog open at one unit: { fileId, unitId }.
    const [reviewTarget, setReviewTarget] = useState(null);
    const [notice, setNotice] = useState(null);

    const isAdd = lineage.policy === ADD_POLICY;

    // The belief in the form the grid shows a value (DataTable.js::getCellValue):
    // a declared date as a calendar day, a number to two decimal places, anything
    // else as stored. The cell's declared type is its metric leaf's, found by the
    // leaf's dotted path in the metric schema, or its detail or attribute
    // column's. The belief is shown when the grid shows another value: a grid
    // read made before a later landing or deletion holds the earlier value, and
    // so does a row no landing has recomputed since its column's policy changed
    // (Spec 4 section 7).
    const declaredType = lineage.period_key === null
        ? [...table.detailColumns, ...table.attributeColumns]
            .find((definition) => definition.name === lineage.column).type
        : lineage.column.split('.').reduce((schema, name) => schema[name], table.metricColumns.schema).type;
    let beliefAsShown = lineage.belief;
    if (isDateType(declaredType)) {
        beliefAsShown = formatDeclaredDate(lineage.belief);
    } else if (typeof lineage.belief === 'number') {
        beliefAsShown = numberFormatter.format(lineage.belief);
    }

    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                    Policy: {POLICY_NAMES[lineage.policy]}
                </Typography>
                {lineage.policy === RECENT_WEIGHTED_POLICY && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Half-life {countNoun(lineage.reason.half_life_days, 'day')}
                        {lineage.reason.anchor !== null
                            && ` · file ages counted from ${formatDateOnly(lineage.reason.anchor)}`}
                    </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1 }}>{lineage.reason.text}</Typography>
                {beliefAsShown !== currentValue && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Value from these files:{' '}
                        <Typography component="span" fontWeight={600}>
                            {beliefAsShown === null ? '-' : formatDisplayValue(beliefAsShown)}
                        </Typography>
                    </Typography>
                )}
            </Paper>
            {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

            {lineage.entries.length === 0 ? (
                <Typography color="text.secondary" textAlign="center">
                    No contributions found.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {lineage.entries.map((entry) => (
                        <FileEntry
                            key={entry.file_id}
                            entry={entry}
                            belief={lineage.belief}
                            isAdd={isAdd}
                            onOpenUnit={(fileId, unitId) => setReviewTarget({ fileId, unitId })}
                        />
                    ))}
                </Stack>
            )}

            {reviewTarget && (
                <FileReviewDialog
                    key={`${reviewTarget.fileId}/${reviewTarget.unitId}`}
                    open
                    companyId={companyId}
                    tableId={tableId}
                    table={table}
                    fileId={reviewTarget.fileId}
                    initialUnitId={reviewTarget.unitId}
                    onClose={() => setReviewTarget(null)}
                    onNotice={setNotice}
                />
            )}
        </Box>
    );
}

export default LineageTimeline;
